import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router";
import { Spinner } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { DemoBanner } from "../demo/DemoBanner";
import { useStaffContext } from "./useStaffContext";
import { VenueProvider } from "./VenueContext";
import { EntitlementsProvider, useVenueEntitlementsQuery } from "./entitlements";
import { GuaranteePolicyProvider, useGuaranteePolicyQuery } from "./guaranteePolicy";
import { VenueRealtimeProvider } from "./VenueRealtimeProvider";
import { ActingStaffProvider } from "./ActingStaffProvider";
import { AppHeader } from "./AppHeader";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { SetupBanner } from "./SetupBanner";
import { UpgradeBanner } from "./UpgradeBanner";
import { SideNav } from "./SideNav";
import { SkipLink } from "./SkipLink";
import { rememberLastVenue } from "./lastVenue";

/**
 * The `/v/:venueId` layout — the product shell every staff surface mounts into. It resolves the path
 * segment against the bootstrap query; an unknown or unassigned id redirects to the picker. It owns
 * the ONE venue-scoped SSE subscription per tab (remounted on venue switch via the `key`), the venue
 * + acting-staff context, and the header/nav/landmark chrome. `<main id="main">` is the skip-link and
 * route-focus target.
 */
export function VenueShellLayout() {
  const { venueId } = useParams();
  const { venues, isLoading, isError } = useStaffContext();
  const { t } = useTranslation();
  const venue = venues.find((v) => v.id === venueId) ?? null;

  // Mobile nav drawer (below md). Closed on every navigation so tapping a link dismisses it — adjusted
  // directly during render (not via an effect) per the React-endorsed "reset state when a prop changes"
  // pattern, comparing against the last-seen pathname.
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  const [lastPathname, setLastPathname] = useState(pathname);
  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    setNavOpen(false);
  }

  // Load the venue's enabled features at shell start so nav + route guards gate on them without a flash
  // (the spinner covers this load too). A read error is non-fatal: `EntitlementsProvider` fails OPEN.
  const entitlements = useVenueEntitlementsQuery(venueId ?? "");
  // Same idea for the venue no-show guarantee policy: loaded at shell start so the deposits / guarantees
  // nav entries + their route guards gate without a flash. On a read glitch it defaults to hidden.
  const guaranteePolicy = useGuaranteePolicyQuery(venueId ?? "");

  useEffect(() => {
    if (venue) rememberLastVenue(venue.id);
  }, [venue]);

  if (isLoading || entitlements.isLoading || guaranteePolicy.isLoading) {
    return (
      <div className={css({ display: "flex", justifyContent: "center", p: "16" })}>
        <Spinner aria-label={t("shell.loading")} />
      </div>
    );
  }
  if (isError) {
    return (
      <main id="main" role="alert" className={css({ p: "8" })}>
        {t("shell.bootstrapError")}
      </main>
    );
  }
  // Unknown or unassigned venue → back to the picker (deep-link safety).
  if (!venue) return <Navigate to="/select-venue" replace />;

  return (
    <VenueProvider value={{ venueId: venue.id, venue }}>
      <EntitlementsProvider flags={entitlements.data?.flags}>
        <GuaranteePolicyProvider policy={guaranteePolicy.data}>
          <ActingStaffProvider>
            <VenueRealtimeProvider key={venue.id} venueId={venue.id}>
              <div className={css({ minH: "100dvh", display: "flex", flexDirection: "column" })}>
                <SkipLink />
                {/* Non-dismissible operator-impersonation banner (R4): staff see when an operator is inside. */}
                <ImpersonationBanner />
                <AppHeader navOpen={navOpen} onMenuToggle={() => setNavOpen((o) => !o)} />
                {/* Persistent, non-dismissible DEMO indicator — renders only on a demo venue (isDemo). */}
                <DemoBanner />
                {/* Persistent setup banner — shown until the venue is publicly LIVE. */}
                <SetupBanner />
                {/* Persistent upgrade nudge (account root) — shown until on a paid plan; independent of setup. */}
                <UpgradeBanner />
                <div className={css({ display: "flex", flex: "1", minH: "0" })}>
                  <SideNav open={navOpen} onClose={() => setNavOpen(false)} />
                  <main
                    id="main"
                    tabIndex={-1}
                    className={css({ flex: "1", outline: "none", overflow: "auto" })}
                  >
                    <Outlet />
                  </main>
                </div>
              </div>
            </VenueRealtimeProvider>
          </ActingStaffProvider>
        </GuaranteePolicyProvider>
      </EntitlementsProvider>
    </VenueProvider>
  );
}
